import styled, { css } from "styled-components";

export const Photos = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 12px 14px 0;
`;

export const SearchRow = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SearchContainer = styled.label`
  flex: 1;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${props => props.theme.colors.grey03};
  border-radius: 12px;
  background-color: ${props => props.theme.colors.grey05};
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;

  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.white};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight};
  }
`;

export const SearchIcon = styled.i.attrs({ className: "icon-search" })`
  color: ${props => props.theme.colors.text03};
  font-size: 16px;
  line-height: 1;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.theme.colors.text01};

  &::placeholder {
    color: ${props => props.theme.colors.text03};
  }
`;

export const RefreshButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${props => props.theme.colors.grey03};
  border-radius: 12px;
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text02};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.grey01};
    color: ${props => props.theme.colors.text01};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryLight};
  }

  i {
    font-size: 16px;
    line-height: 1;
  }
`;

export const PhotoScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 12px -14px 0;
  padding: 0 14px 14px;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.colors.grey04} transparent;
`;

export const EmptyList = styled.div`
  padding: 32px 0;
  text-align: center;
  font-size: 13px;
  color: ${props => props.theme.colors.text02};
`;

export const ImageList = styled.div`
  width: 100%;

  .end-of-list {
    width: 1px;
    height: 1px;
    display: flex;
    visibility: hidden;
    justify-content: center;
  }
`;

export const ImageLinkMeta = styled.i`
  position: absolute;
  display: none;
  top: 8px;
  right: 8px;
  font-size: 10px;
  color: ${props => props.theme.colors.white};
  padding: 2px;
  border-radius: 3px;
  z-index: 1;
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
    color: ${props => props.theme.colors.black};
  }
`;

export const ImageUserMeta = styled.span`
  position: absolute;
  display: none;
  bottom: 8px;
  left: 8px;
  font-size: 10px;
  color: ${props => props.theme.colors.black};
  padding: 2px;
  border-radius: 3px;
  z-index: 1;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.6);
  max-width: calc((100% - 2px) / 1.5);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

export const ImageCard = styled.div<{
  src: string;
  ratio: number;
  disabled: boolean;
  isUploading: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  float: left;
  margin-bottom: 10px;
  width: calc((100% - 10px) / 2);
  height: ${props => (props.ratio < 0.7 ? 110 : 190)}px;
  box-shadow: ${props => props.theme.shadows.shadowXS};
  border-radius: 12px;
  background: url("${props => props.src}") no-repeat scroll center top
    ${props => props.theme.colors.white};
  background-size: cover;
  cursor: pointer;

  ${props =>
    props.disabled &&
    css`
      cursor: progress;
    `};

  ${props =>
    props.isUploading &&
    css`
      border: unset;

      &:after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 100%;
        background-color: ${props => props.theme.colors.white};
        opacity: 80%;
        border-radius: 12px;
      }
    `};

  &:hover {
    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 100%;
      background-color: ${props => props.theme.colors.black};
      opacity: 40%;
      border-radius: 12px;
    }

    ${ImageUserMeta} {
      display: block;
    }

    ${ImageLinkMeta} {
      display: block;
    }
  }
`;

export const ImageCardShimmer = styled.div<{ index: number }>`
  box-sizing: border-box;
  float: left;
  margin-bottom: 10px;
  width: calc((100% - 10px) / 2);
  height: ${props => (props.index % 2 === 0 ? 110 : 190)}px;
  border: 1px solid ${props => props.theme.colors.grey01};
  border-radius: 12px;

  animation-duration: 1.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: shimmer;
  animation-timing-function: linear;
  background: white;
  background: linear-gradient(to right, #f6f6f6 8%, #f0f0f0 18%, #f6f6f6 33%);
  background-size: 1200px 100%;

  @keyframes shimmer {
    0% {
      background-position: -1200px 0;
    }
    100% {
      background-position: 1200px 0;
    }
  }
`;
